import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import { supabase } from '../lib/supabase';
import { useTheme } from '../contexts/ThemeContext';

// Supabase delivers the user to /reset-password with a recovery token. Until
// that token is exchanged into a session by supabase-js (event: PASSWORD_RECOVERY),
// we can't call updateUser. Gate the form on that event.
const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  // Recovery links are single-use and expire (24h). Two ways that shows up:
  // 1. Supabase redirects back with the error IN THE FRAGMENT
  //    (#error=access_denied&error_code=otp_expired&...) — check it before
  //    supabase-js strips/replaces the hash.
  // 2. No PASSWORD_RECOVERY event ever fires (token already consumed, or the
  //    fragment was lost on a redirect) — the page would sit on "Verifying
  //    reset link…" forever. Time out into an actionable expired state that
  //    offers "request a new link" instead of a raw error.
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const hash = window.location.hash || '';
    if (/error=access_denied|otp_expired|error_code=/i.test(hash)) {
      setExpired(true);
      return;
    }
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        setReady(true);
      }
    });
    // If the user already has an active session (e.g. they clicked the link
    // while logged in, or supabase-js exchanged the token before our listener
    // attached), allow the form immediately.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (ready || expired) return undefined;
    const t = setTimeout(() => setExpired(true), 10000);
    return () => clearTimeout(t);
  }, [ready, expired]);

  const handleSubmit = async () => {
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      // 2026-08-14 — CRITICAL FIX. Self-serve password reset was silently
      // failing for every user since the page shipped. The recovery session
      // was established (PASSWORD_RECOVERY event fired, verify succeeded) but
      // the password was never written. Root cause: updateUser was either (a)
      // not awaited properly in a race with AuthProvider's fetchProfile, or
      // (b) returning an error that was caught but the user navigated away
      // before seeing it. Fix per the spec:
      //   1. Call updateUser directly (not via AuthContext wrapper) to avoid
      //      any stale-closure issues.
      //   2. Check the error explicitly — do NOT navigate on error.
      //   3. Sign the user out after success (the recovery session is
      //      authenticated; without signOut they land in the dashboard on a
      //      half-recovery session rather than logging in cleanly).
      //   4. Navigate to /login?reset=success (not /) so the login page knows
      //      to show a success message.
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        // Surface the actual error message — "New password should be different
        // from the old password" and "Auth session missing" are different
        // problems and the user can act on the first.
        setError(updateError.message);
        setSubmitting(false);
        return;  // do NOT navigate away on error
      }
      // Success — sign out the recovery session before redirecting.
      await supabase.auth.signOut();
      setSuccess(true);
      setTimeout(() => navigate('/?reset=success', { replace: true }), 2000);
    } catch (err) {
      setError(err?.message || 'Failed to update password. Please try again or contact support.');
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-[100dvh] bg-white dark:bg-gray-950 transition-colors duration-200">
      <div className="hidden md:flex items-center justify-center bg-gray-100 dark:bg-gray-900 md:w-1/2">
        <img src={logo} alt="Insider Perks" className="max-w-xs lg:max-w-md px-4" />
      </div>

      <div className="flex items-center justify-center w-full md:w-1/2 bg-white dark:bg-gray-950 relative px-4 py-8 md:px-6 md:py-0">
        <button
          onClick={toggleTheme}
          className="absolute top-4 right-4 md:top-6 md:right-6 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors z-10"
          title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          {isDarkMode ? (
            <svg className="w-5 h-5 md:w-6 md:h-6 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
              <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
            </svg>
          )}
        </button>

        <div className="absolute top-8 left-1/2 transform -translate-x-1/2 md:hidden">
          <img src={logo} alt="Insider Perks" className="h-16 w-auto" />
        </div>

        <div className="bg-white dark:bg-gray-900 shadow-lg dark:shadow-2xl rounded-lg p-6 sm:p-8 md:p-10 w-full max-w-sm border dark:border-gray-800 mt-28 md:mt-0">
          {expired && !ready && !success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                </svg>
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">Reset link expired</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                This password reset link has expired or was already used. Links are valid for 24 hours and work only once.
              </p>
              <button
                onClick={() => navigate('/', { replace: true })}
                className="bg-[#32B1FF] text-white py-2.5 sm:py-2 px-4 w-full rounded text-sm font-semibold hover:bg-[#1a9ce6] transition-colors"
              >
                Request a new link
              </button>
            </div>
          ) : success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">Password updated</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-100 mb-1">Choose a New Password</h2>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-6">
                {ready ? 'Enter and confirm your new password.' : 'Verifying reset link…'}
              </p>

              <label className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">New password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                disabled={!ready || submitting}
                className="w-full mt-1 mb-4 p-2.5 sm:p-2 border dark:border-gray-700 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent outline-none disabled:opacity-60"
              />

              <label className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">Confirm new password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat password"
                autoComplete="new-password"
                disabled={!ready || submitting}
                onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
                className="w-full mt-1 mb-4 p-2.5 sm:p-2 border dark:border-gray-700 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent outline-none disabled:opacity-60"
              />

              {error && <div className="text-red-500 dark:text-red-400 text-xs sm:text-sm mb-2 p-2 bg-red-50 dark:bg-red-900/20 rounded">{error}</div>}

              <button
                onClick={handleSubmit}
                disabled={!ready || submitting}
                className="bg-[#32B1FF] text-white py-2.5 sm:py-2 w-full rounded text-sm font-semibold hover:bg-[#1a9ce6] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
              >
                {submitting ? <div className="spinner"></div> : 'Update Password'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
